import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { CoverEffectType, CoverLayout } from 'fym-shared/src/music24/coverDoc';
import type { CoverAiPictureFailCode } from '../../redux/api/fymAPI.types';
import type { CoverAiRunError } from './doc/coverEditorErrors';
import type { CoverUploadError } from './panels/picture/useReferenceUpload';
import type { UseAsCoverError } from './topbar/useUseAsCover';

/** The copy of the cover editor (design cover-editor b to f). */
export const coverEditorMessages = defineMessages({
  // The top bar
  back: { id: 'app.sml.coverEditor.back', defaultMessage: 'Back' },
  songLine: { id: 'app.sml.coverEditor.songLine', defaultMessage: '{artist} · cover art · {size} × {size}' },
  undo: { id: 'app.sml.coverEditor.undo', defaultMessage: 'Undo' },
  redo: { id: 'app.sml.coverEditor.redo', defaultMessage: 'Redo' },
  zoomFit: { id: 'app.sml.coverEditor.zoom.fit', defaultMessage: 'Fit' },
  zoomValue: { id: 'app.sml.coverEditor.zoom.value', defaultMessage: '{percent}%' },
  zoomIn: { id: 'app.sml.coverEditor.zoom.in', defaultMessage: 'Zoom in' },
  zoomOut: { id: 'app.sml.coverEditor.zoom.out', defaultMessage: 'Zoom out' },
  saveSaved: { id: 'app.sml.coverEditor.save.saved', defaultMessage: 'Saved · version {version}' },
  saveDirty: { id: 'app.sml.coverEditor.save.dirty', defaultMessage: 'Not saved yet' },
  saveSaving: { id: 'app.sml.coverEditor.save.saving', defaultMessage: 'Saving' },
  saveFailed: {
    id: 'app.sml.coverEditor.save.failed',
    defaultMessage: 'Not saved. We try again when you change something.',
  },
  saveInvalid: {
    id: 'app.sml.coverEditor.save.invalid',
    defaultMessage: 'This change cannot be saved. Undo it to keep saving.',
  },
  saveReadOnly: { id: 'app.sml.coverEditor.save.readOnly', defaultMessage: 'Example artist · nothing is saved' },
  planAi: { id: 'app.sml.coverEditor.plan.ai', defaultMessage: 'AI pictures included' },
  planBank: { id: 'app.sml.coverEditor.plan.bank', defaultMessage: 'Picture bank included' },
  download: { id: 'app.sml.coverEditor.download', defaultMessage: 'Download' },
  useAsCover: { id: 'app.sml.coverEditor.useAsCover', defaultMessage: 'Use as cover' },

  // The tools
  toolsLabel: { id: 'app.sml.coverEditor.tools.label', defaultMessage: 'Tools' },
  toolPicture: { id: 'app.sml.coverEditor.tools.picture', defaultMessage: 'Picture' },
  toolText: { id: 'app.sml.coverEditor.tools.text', defaultMessage: 'Text' },
  toolEffects: { id: 'app.sml.coverEditor.tools.effects', defaultMessage: 'Effects' },
  toolLayers: { id: 'app.sml.coverEditor.tools.layers', defaultMessage: 'Layers' },
  tabAi: { id: 'app.sml.coverEditor.picture.tab.ai', defaultMessage: 'AI' },
  tabBank: { id: 'app.sml.coverEditor.picture.tab.bank', defaultMessage: 'Bank' },
  tabUpload: { id: 'app.sml.coverEditor.picture.tab.upload', defaultMessage: 'Upload' },
  tabColour: { id: 'app.sml.coverEditor.picture.tab.colour', defaultMessage: 'Colour' },

  // The AI tab
  aiQuestion: { id: 'app.sml.coverEditor.ai.question', defaultMessage: 'What should the picture show?' },
  aiDefaultInstructions: {
    id: 'app.sml.coverEditor.ai.defaultInstructions',
    defaultMessage: 'A cover picture for the song “{title}”. {feeling}',
  },
  aiGenerate: { id: 'app.sml.coverEditor.ai.generate', defaultMessage: 'Make {count} pictures' },
  aiWorking: { id: 'app.sml.coverEditor.ai.working', defaultMessage: 'Making {count} pictures' },
  aiNoText: {
    id: 'app.sml.coverEditor.ai.noText',
    defaultMessage:
      'The pictures have no text. You add the title and the artist as text boxes, so the spelling is always right.',
  },
  aiMaking: { id: 'app.sml.coverEditor.ai.making', defaultMessage: 'In the making' },
  aiMade: { id: 'app.sml.coverEditor.ai.made', defaultMessage: 'Newest pictures' },
  aiEarlier: { id: 'app.sml.coverEditor.ai.earlier', defaultMessage: 'Earlier' },
  aiQueued: { id: 'app.sml.coverEditor.ai.queued', defaultMessage: 'In the queue' },
  aiPainting: { id: 'app.sml.coverEditor.ai.painting', defaultMessage: 'Painting' },
  aiPictureFailed: { id: 'app.sml.coverEditor.ai.pictureFailed', defaultMessage: 'Did not work' },
  aiPictureLabel: { id: 'app.sml.coverEditor.ai.pictureLabel', defaultMessage: 'AI picture {number}' },
  aiNotCounted: {
    id: 'app.sml.coverEditor.ai.notCounted',
    defaultMessage: 'These pictures do not count toward your month.',
  },
  aiMeterLabel: { id: 'app.sml.coverEditor.ai.meter.label', defaultMessage: 'AI pictures this month' },
  aiMeterValue: { id: 'app.sml.coverEditor.ai.meter.value', defaultMessage: '{used} of {limit}' },
  aiMeterFull: {
    id: 'app.sml.coverEditor.ai.meter.full',
    defaultMessage:
      'You used your AI pictures for this month. New ones come on {date}. The bank, your own pictures and every edit still work.',
  },
  aiLockedTitle: { id: 'app.sml.coverEditor.ai.locked.title', defaultMessage: 'Get a picture made for your song' },
  aiLockedText: {
    id: 'app.sml.coverEditor.ai.locked.text',
    defaultMessage:
      'A paid plan makes AI pictures from your song, from {count} a month, with no credits. Your free plan has the picture bank, your own uploads, text and effects.',
  },
  aiLockedUpgrade: { id: 'app.sml.coverEditor.ai.locked.upgrade', defaultMessage: 'See the plans' },
  aiLockedBank: { id: 'app.sml.coverEditor.ai.locked.bank', defaultMessage: 'Pick from the bank' },
  // The welcome run of the trial (owner request 2026-10-09: "the first 4 images are on us")
  aiWelcomeGenerate: { id: 'app.sml.coverEditor.ai.welcome.generate', defaultMessage: 'Make {count} pictures, on us' },
  aiWelcomeHint: {
    id: 'app.sml.coverEditor.ai.welcome.hint',
    defaultMessage: 'Your first {count} AI pictures are on us. After them, AI pictures come with a paid plan.',
  },
  themeStepEyebrow: { id: 'app.sml.coverEditor.themeStep.eyebrow', defaultMessage: 'Step 1 of 2' },
  themeStepTitle: { id: 'app.sml.coverEditor.themeStep.title', defaultMessage: 'Pick the feeling of your cover' },
  themeStepText: {
    id: 'app.sml.coverEditor.themeStep.text',
    defaultMessage:
      'Then we make {count} AI pictures for {title} in this feeling, on us. You can change the picture, the text and the feeling after.',
  },
  themeStepContinue: { id: 'app.sml.coverEditor.themeStep.continue', defaultMessage: 'Make my {count} pictures' },
  themeStepFailed: {
    id: 'app.sml.coverEditor.themeStep.failed',
    defaultMessage: 'We could not save the feeling. Try again.',
  },
  referenceAdd: { id: 'app.sml.coverEditor.ai.reference.add', defaultMessage: 'Add a picture to follow' },
  referenceRemove: { id: 'app.sml.coverEditor.ai.reference.remove', defaultMessage: 'Remove this picture' },
  referenceHint: {
    id: 'app.sml.coverEditor.ai.reference.hint',
    defaultMessage: 'Add up to {count} pictures to follow. Optional.',
  },
  rightsTitle: { id: 'app.sml.coverEditor.rights.title', defaultMessage: 'Can you use these pictures?' },
  rightsText: {
    id: 'app.sml.coverEditor.rights.text',
    defaultMessage:
      'We ask one time for each artist. The AI follows the style, the colours and the subject of your pictures.',
  },
  rightsRuleOwn: {
    id: 'app.sml.coverEditor.rights.rule.own',
    defaultMessage: 'The pictures are yours, or you have the permission of the person who made them.',
  },
  rightsRulePeople: {
    id: 'app.sml.coverEditor.rights.rule.people',
    defaultMessage: 'People in the pictures agree to this use.',
  },
  rightsRuleUse: {
    id: 'app.sml.coverEditor.rights.rule.use',
    defaultMessage: 'MUSIC24 uses the pictures only to make your cover.',
  },
  rightsTick: {
    id: 'app.sml.coverEditor.rights.tick',
    defaultMessage: 'I have the rights to use these pictures for {artist}.',
  },
  rightsConfirm: { id: 'app.sml.coverEditor.rights.confirm', defaultMessage: 'Add the pictures' },

  // The Bank, Upload and Colour tabs
  bankAll: { id: 'app.sml.coverEditor.bank.all', defaultMessage: 'All' },
  bankThemes: { id: 'app.sml.coverEditor.bank.themes', defaultMessage: 'Themes' },
  bankMoods: { id: 'app.sml.coverEditor.bank.moods', defaultMessage: 'Moods' },
  bankPictureLabel: { id: 'app.sml.coverEditor.bank.pictureLabel', defaultMessage: 'Bank picture {number}' },
  bankEmpty: {
    id: 'app.sml.coverEditor.bank.empty',
    defaultMessage: 'The picture bank is not available now. Use your own picture or a colour.',
  },
  bankNoMatch: {
    id: 'app.sml.coverEditor.bank.noMatch',
    defaultMessage: 'No picture for this choice. Try another theme or mood.',
  },
  bankHint: {
    id: 'app.sml.coverEditor.bank.hint',
    defaultMessage: '{count, plural, one {# picture} other {# pictures}}, free on every plan.',
  },
  uploadTitle: { id: 'app.sml.coverEditor.upload.title', defaultMessage: 'Drop a picture here' },
  uploadRules: { id: 'app.sml.coverEditor.upload.rules', defaultMessage: 'JPG, PNG or WebP, up to 25 MB.' },
  uploadPick: { id: 'app.sml.coverEditor.upload.pick', defaultMessage: 'Choose a picture' },
  uploadHint: {
    id: 'app.sml.coverEditor.upload.hint',
    defaultMessage: 'A square picture of 3000 px or more is best. Click the cover to move and zoom the picture.',
  },
  colourKind: { id: 'app.sml.coverEditor.colour.kind', defaultMessage: 'Kind of colour' },
  colourSolid: { id: 'app.sml.coverEditor.colour.solid', defaultMessage: 'One colour' },
  colourGradient: { id: 'app.sml.coverEditor.colour.gradient', defaultMessage: 'Gradient' },
  colourFrom: { id: 'app.sml.coverEditor.colour.from', defaultMessage: 'From' },
  colourTo: { id: 'app.sml.coverEditor.colour.to', defaultMessage: 'To' },
  colourAngle: { id: 'app.sml.coverEditor.colour.angle', defaultMessage: 'Angle' },
  colourCustom: { id: 'app.sml.coverEditor.colour.custom', defaultMessage: '{name}: any colour' },
  colourHint: {
    id: 'app.sml.coverEditor.colour.hint',
    defaultMessage: 'The colours come from your cover. “+” takes any colour.',
  },
  colourReplacesPicture: {
    id: 'app.sml.coverEditor.colour.replacesPicture',
    defaultMessage: 'A colour replaces the picture. Undo brings the picture back.',
  },

  // The Text tool and the text inspector
  textAdd: { id: 'app.sml.coverEditor.text.add', defaultMessage: 'Add a text box' },
  textNewBox: { id: 'app.sml.coverEditor.text.newBox', defaultMessage: 'Your text' },
  textLimit: { id: 'app.sml.coverEditor.text.limit', defaultMessage: 'A cover has at most {count} text boxes.' },
  textLayouts: { id: 'app.sml.coverEditor.text.layouts', defaultMessage: 'Layouts' },
  textLayoutsHint: {
    id: 'app.sml.coverEditor.text.layoutsHint',
    defaultMessage: 'A layout places the title and the artist. Your own text boxes stay.',
  },
  textEditLabel: { id: 'app.sml.coverEditor.text.editLabel', defaultMessage: 'The words of the text box' },
  inspectorText: { id: 'app.sml.coverEditor.inspector.text', defaultMessage: 'Text' },
  inspectorEffect: { id: 'app.sml.coverEditor.inspector.effect', defaultMessage: 'Effect' },
  inspectorCover: { id: 'app.sml.coverEditor.inspector.cover', defaultMessage: 'Cover' },
  roleTitle: { id: 'app.sml.coverEditor.role.title', defaultMessage: 'Title' },
  roleArtist: { id: 'app.sml.coverEditor.role.artist', defaultMessage: 'Artist' },
  roleFree: { id: 'app.sml.coverEditor.role.free', defaultMessage: 'Your text' },
  resetToTheme: { id: 'app.sml.coverEditor.text.resetToTheme', defaultMessage: 'Reset to theme' },
  textFont: { id: 'app.sml.coverEditor.text.font', defaultMessage: 'Font' },
  textFontTheme: { id: 'app.sml.coverEditor.text.fontTheme', defaultMessage: 'Theme' },
  textSize: { id: 'app.sml.coverEditor.text.size', defaultMessage: 'Size' },
  textWeight: { id: 'app.sml.coverEditor.text.weight', defaultMessage: 'Weight' },
  textColour: { id: 'app.sml.coverEditor.text.colour', defaultMessage: 'Colour' },
  textAlign: { id: 'app.sml.coverEditor.text.align', defaultMessage: 'Align' },
  alignLeft: { id: 'app.sml.coverEditor.text.align.left', defaultMessage: 'Left' },
  alignCentre: { id: 'app.sml.coverEditor.text.align.centre', defaultMessage: 'Centre' },
  alignRight: { id: 'app.sml.coverEditor.text.align.right', defaultMessage: 'Right' },
  textCase: { id: 'app.sml.coverEditor.text.case', defaultMessage: 'Case' },
  textSpacing: { id: 'app.sml.coverEditor.text.spacing', defaultMessage: 'Spacing' },
  textLine: { id: 'app.sml.coverEditor.text.line', defaultMessage: 'Line' },
  textShadow: { id: 'app.sml.coverEditor.text.shadow', defaultMessage: 'Shadow' },
  textOutline: { id: 'app.sml.coverEditor.text.outline', defaultMessage: 'Outline' },
  textGlow: { id: 'app.sml.coverEditor.text.glow', defaultMessage: 'Glow' },
  textOpacity: { id: 'app.sml.coverEditor.text.opacity', defaultMessage: 'Opacity' },
  textRotate: { id: 'app.sml.coverEditor.text.rotate', defaultMessage: 'Rotate' },
  textPosition: { id: 'app.sml.coverEditor.text.position', defaultMessage: 'Position' },
  textHint: {
    id: 'app.sml.coverEditor.text.hint',
    defaultMessage: 'The colours come from the picture. The fonts are the 9 fonts of your release themes.',
  },
  toolbarLabel: { id: 'app.sml.coverEditor.toolbar.label', defaultMessage: 'Text box' },
  toolbarSmaller: { id: 'app.sml.coverEditor.toolbar.smaller', defaultMessage: 'Smaller text' },
  toolbarLarger: { id: 'app.sml.coverEditor.toolbar.larger', defaultMessage: 'Larger text' },
  toolbarBold: { id: 'app.sml.coverEditor.toolbar.bold', defaultMessage: 'B' },
  toolbarCase: { id: 'app.sml.coverEditor.toolbar.case', defaultMessage: 'Change the case' },
  toolbarCopy: { id: 'app.sml.coverEditor.toolbar.copy', defaultMessage: 'Copy' },
  toolbarDelete: { id: 'app.sml.coverEditor.toolbar.delete', defaultMessage: 'Delete' },
  handleRotate: { id: 'app.sml.coverEditor.handle.rotate', defaultMessage: 'Turn the text box' },

  // The effects and the layers
  effectStrength: { id: 'app.sml.coverEditor.effect.strength', defaultMessage: 'Strength' },
  effectColour: { id: 'app.sml.coverEditor.effect.colour', defaultMessage: 'Colour {number}' },
  effectLayer: { id: 'app.sml.coverEditor.effect.layer', defaultMessage: 'Place' },
  effectUnderText: { id: 'app.sml.coverEditor.effect.underText', defaultMessage: 'Under text' },
  effectOverText: { id: 'app.sml.coverEditor.effect.overText', defaultMessage: 'Over text' },
  effectRemove: { id: 'app.sml.coverEditor.effect.remove', defaultMessage: 'Turn off' },
  effectOrder: { id: 'app.sml.coverEditor.effect.order', defaultMessage: 'Order' },
  effectsHint: {
    id: 'app.sml.coverEditor.effect.hint',
    defaultMessage:
      'MUSIC24 draws the effects, so the screen and your file are the same. Click a tile for its settings.',
  },
  layersText: { id: 'app.sml.coverEditor.layers.text', defaultMessage: 'Text' },
  layersEffects: { id: 'app.sml.coverEditor.layers.effects', defaultMessage: 'Effects' },
  layersBackground: { id: 'app.sml.coverEditor.layers.background', defaultMessage: 'Background' },
  layersNoText: { id: 'app.sml.coverEditor.layers.noText', defaultMessage: 'No text box. Add one in Text.' },
  layersNoEffects: { id: 'app.sml.coverEditor.layers.noEffects', defaultMessage: 'No effect. Turn one on in Effects.' },
  layerEmptyText: { id: 'app.sml.coverEditor.layers.emptyText', defaultMessage: 'Empty text box' },
  layerPicture: { id: 'app.sml.coverEditor.layers.picture', defaultMessage: 'Picture' },
  layerColour: { id: 'app.sml.coverEditor.layers.colour', defaultMessage: 'One colour' },
  layerGradient: { id: 'app.sml.coverEditor.layers.gradient', defaultMessage: 'Gradient' },
  layerUp: { id: 'app.sml.coverEditor.layers.up', defaultMessage: 'Move “{name}” up' },
  layerDown: { id: 'app.sml.coverEditor.layers.down', defaultMessage: 'Move “{name}” down' },
  layerRemove: { id: 'app.sml.coverEditor.layers.remove', defaultMessage: 'Remove “{name}”' },

  // The cover inspector and the stage
  coverSize: { id: 'app.sml.coverEditor.cover.size', defaultMessage: 'Size' },
  coverSizeValue: { id: 'app.sml.coverEditor.cover.sizeValue', defaultMessage: '{size} × {size} px · JPEG' },
  coverTheme: { id: 'app.sml.coverEditor.cover.theme', defaultMessage: 'Theme' },
  coverColours: { id: 'app.sml.coverEditor.cover.colours', defaultMessage: 'Colours' },
  coverLayers: { id: 'app.sml.coverEditor.cover.layers', defaultMessage: 'On it' },
  coverLayersValue: {
    id: 'app.sml.coverEditor.cover.layersValue',
    defaultMessage:
      '{texts, plural, one {# text box} other {# text boxes}} · {effects, plural, one {# effect} other {# effects}}',
  },
  coverHint: {
    id: 'app.sml.coverEditor.cover.hint',
    defaultMessage: 'Click a text on the cover to change it. Double-click to type.',
  },
  pictureZoom: { id: 'app.sml.coverEditor.picture.zoom', defaultMessage: 'Zoom' },
  pictureFocusX: { id: 'app.sml.coverEditor.picture.focusX', defaultMessage: 'Left–right' },
  pictureFocusY: { id: 'app.sml.coverEditor.picture.focusY', defaultMessage: 'Up–down' },
  pictureFailed: {
    id: 'app.sml.coverEditor.picture.failed',
    defaultMessage: 'The picture did not load. Pick another picture.',
  },
  dimensions: {
    id: 'app.sml.coverEditor.stage.dimensions',
    defaultMessage: '{size} × {size} px · JPEG · no watermark',
  },
  dimensionsWatermark: {
    id: 'app.sml.coverEditor.stage.dimensionsWatermark',
    defaultMessage: '{size} × {size} px · JPEG · MUSIC24 watermark until a paid plan',
  },
  guidesOn: { id: 'app.sml.coverEditor.stage.guidesOn', defaultMessage: 'Guides on' },
  guidesOff: { id: 'app.sml.coverEditor.stage.guidesOff', defaultMessage: 'Guides off' },
  safeMargin: { id: 'app.sml.coverEditor.stage.safeMargin', defaultMessage: 'Safe margin' },
  previewSmall: { id: 'app.sml.coverEditor.stage.previewSmall', defaultMessage: 'Preview small: {size} px' },
  previewSmallLabel: {
    id: 'app.sml.coverEditor.stage.previewSmallLabel',
    defaultMessage: 'The cover at the size of a track list',
  },
  storeNote: {
    id: 'app.sml.coverEditor.storeNote',
    defaultMessage:
      'Your cover has text that is not the song title or the artist name. Stores can refuse such a cover.',
  },
  readOnly: {
    id: 'app.sml.coverEditor.readOnly',
    defaultMessage: 'This is the example artist. You can look at every tool, but nothing changes.',
  },
  noticeDismiss: { id: 'app.sml.coverEditor.notice.dismiss', defaultMessage: 'OK' },

  // The versions
  versions: { id: 'app.sml.coverEditor.versions.title', defaultMessage: 'Versions' },
  versionsCount: {
    id: 'app.sml.coverEditor.versions.count',
    defaultMessage: '{count} saved · all kept',
  },
  versionOpen: { id: 'app.sml.coverEditor.versions.open', defaultMessage: 'Open version {number}' },
  versionDelete: { id: 'app.sml.coverEditor.versions.delete', defaultMessage: 'Delete version {number}' },
  versionNumber: { id: 'app.sml.coverEditor.versions.number', defaultMessage: '{number}' },
  versionUsed: { id: 'app.sml.coverEditor.versions.used', defaultMessage: '{number} · used' },
  versionCoverNow: { id: 'app.sml.coverEditor.versions.coverNow', defaultMessage: 'Cover now' },
  versionNew: { id: 'app.sml.coverEditor.versions.new', defaultMessage: 'Keep this cover as a new version' },
  versionNewShort: { id: 'app.sml.coverEditor.versions.newShort', defaultMessage: 'New' },
  versionsHint: {
    id: 'app.sml.coverEditor.versions.hint',
    defaultMessage: 'Your design saves by itself. “New” keeps a version, so you can try another idea.',
  },

  // First open, leave, Use as cover
  firstMaking: { id: 'app.sml.coverEditor.first.making', defaultMessage: 'Making your first version…' },
  firstFailed: {
    id: 'app.sml.coverEditor.first.failed',
    defaultMessage: 'We could not make the first version of your cover. Try again.',
  },
  firstAiMaking: {
    id: 'app.sml.coverEditor.first.aiMaking',
    defaultMessage: 'We are making 4 pictures from your song. Until they are ready, this is a picture from the bank.',
  },
  firstAiReady: {
    id: 'app.sml.coverEditor.first.aiReady',
    defaultMessage: 'Your first pictures are ready. Pick another one in Picture, or change anything.',
  },
  firstBank: {
    id: 'app.sml.coverEditor.first.bank',
    defaultMessage: 'We made a first version for you with a picture from the bank. Change anything.',
  },
  firstAiUnavailable: {
    id: 'app.sml.coverEditor.first.aiUnavailable',
    defaultMessage: 'AI pictures are not available now. We started with a picture from the bank.',
  },
  leaveTitle: { id: 'app.sml.coverEditor.leave.title', defaultMessage: 'Save your last changes?' },
  leaveText: {
    id: 'app.sml.coverEditor.leave.text',
    defaultMessage: 'Your last changes are not saved yet. We save them and then leave the editor.',
  },
  leaveTextFailed: {
    id: 'app.sml.coverEditor.leave.textFailed',
    defaultMessage: 'The last save did not work. We try again and then leave. If it fails, you stay here.',
  },
  leaveConfirm: { id: 'app.sml.coverEditor.leave.confirm', defaultMessage: 'Save and leave' },
  leaveStay: { id: 'app.sml.coverEditor.leave.stay', defaultMessage: 'Stay' },
  useTitle: { id: 'app.sml.coverEditor.use.title', defaultMessage: 'Use this as the cover of “{song}”?' },
  useText: {
    id: 'app.sml.coverEditor.use.text',
    defaultMessage: 'It replaces the cover that the song has now. Your design stays here, and you can change it again.',
  },
  useConfirm: { id: 'app.sml.coverEditor.use.confirm', defaultMessage: 'Use as cover' },
  useNow: { id: 'app.sml.coverEditor.use.now', defaultMessage: 'Cover now' },
  useNew: { id: 'app.sml.coverEditor.use.new', defaultMessage: 'New cover' },
  useAssets: {
    id: 'app.sml.coverEditor.use.assets',
    defaultMessage:
      '{count, plural, one {# Studio asset still shows the old cover. Make it again to get the new one.} other {# Studio assets still show the old cover. Make them again to get the new one.}}',
  },
  useWatermark: {
    id: 'app.sml.coverEditor.use.watermark',
    defaultMessage: 'Until you have a paid plan, the cover has the MUSIC24 watermark. A paid plan removes it.',
  },
  useDone: { id: 'app.sml.coverEditor.use.done', defaultMessage: 'Your song has its new cover.' },
});

/** The name and the one-line hint of each effect (design cover-editor d3). */
export const effectMessages: Record<CoverEffectType, { name: MessageDescriptor; hint: MessageDescriptor }> = {
  grain: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.grain.name', defaultMessage: 'Grain' },
    hint: { id: 'app.sml.coverEditor.effect.grain.hint', defaultMessage: 'Fine film noise over the whole cover.' },
  }),
  vignette: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.vignette.name', defaultMessage: 'Vignette' },
    hint: {
      id: 'app.sml.coverEditor.effect.vignette.hint',
      defaultMessage: 'Dark edges that pull the eye to the middle.',
    },
  }),
  circles: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.circles.name', defaultMessage: 'Circles' },
    hint: {
      id: 'app.sml.coverEditor.effect.circles.hint',
      defaultMessage: 'Thin rings and a soft disc in your colours.',
    },
  }),
  neonFrame: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.neonFrame.name', defaultMessage: 'Neon frame' },
    hint: { id: 'app.sml.coverEditor.effect.neonFrame.hint', defaultMessage: 'A glowing line around the cover.' },
  }),
  lightLeak: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.lightLeak.name', defaultMessage: 'Light leak' },
    hint: {
      id: 'app.sml.coverEditor.effect.lightLeak.hint',
      defaultMessage: 'Warm light that comes in from the corners.',
    },
  }),
  halftone: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.halftone.name', defaultMessage: 'Halftone' },
    hint: {
      id: 'app.sml.coverEditor.effect.halftone.hint',
      defaultMessage: 'Print dots, larger where the picture is dark.',
    },
  }),
  duotone: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.duotone.name', defaultMessage: 'Duotone' },
    hint: {
      id: 'app.sml.coverEditor.effect.duotone.hint',
      defaultMessage: 'The picture in two colours: one for dark, one for light.',
    },
  }),
  blur: defineMessages({
    name: { id: 'app.sml.coverEditor.effect.blur.name', defaultMessage: 'Blur' },
    hint: { id: 'app.sml.coverEditor.effect.blur.hint', defaultMessage: 'A soft picture, so the text stands out.' },
  }),
};

/** The names of the four text layouts (design cover-editor d2). */
export const layoutMessages: Record<CoverLayout, MessageDescriptor> = defineMessages({
  bottom: { id: 'app.sml.coverEditor.layout.bottom', defaultMessage: 'Title at the bottom' },
  centre: { id: 'app.sml.coverEditor.layout.centre', defaultMessage: 'Big in the middle' },
  corner: { id: 'app.sml.coverEditor.layout.corner', defaultMessage: 'In the corner' },
  none: { id: 'app.sml.coverEditor.layout.none', defaultMessage: 'No text' },
});

export const uploadErrorMessages: Record<CoverUploadError, MessageDescriptor> = defineMessages({
  type: { id: 'app.sml.coverEditor.upload.error.type', defaultMessage: 'Use a JPG, PNG or WebP picture.' },
  size: { id: 'app.sml.coverEditor.upload.error.size', defaultMessage: 'The picture is larger than 25 MB.' },
  failed: { id: 'app.sml.coverEditor.upload.error.failed', defaultMessage: 'The picture did not upload. Try again.' },
});

/** What the Picture panel says when an AI run does not start (spec §5). */
export const aiRunErrorMessages: Record<CoverAiRunError, MessageDescriptor> = defineMessages({
  noAi: { id: 'app.sml.coverEditor.ai.error.noAi', defaultMessage: 'AI pictures come with a paid plan.' },
  limit: {
    id: 'app.sml.coverEditor.ai.error.limit',
    defaultMessage: 'You have fewer than 4 AI pictures left this month.',
  },
  rights: { id: 'app.sml.coverEditor.ai.error.rights', defaultMessage: 'Confirm that you can use these pictures.' },
  reference: {
    id: 'app.sml.coverEditor.ai.error.reference',
    defaultMessage: 'One of your pictures cannot be used. Remove it and add it again.',
  },
  busy: {
    id: 'app.sml.coverEditor.ai.error.busy',
    defaultMessage: 'Your last pictures are still in the making. Wait a moment.',
  },
  demo: { id: 'app.sml.coverEditor.ai.error.demo', defaultMessage: 'The example artist cannot make pictures.' },
  rate: { id: 'app.sml.coverEditor.ai.error.rate', defaultMessage: 'That was fast. Wait a minute and try again.' },
  failed: { id: 'app.sml.coverEditor.ai.error.failed', defaultMessage: 'The pictures did not start. Try again.' },
});

/** Why the pictures of a run failed. */
export const pictureFailMessages: Record<CoverAiPictureFailCode, MessageDescriptor> = defineMessages({
  IMAGE_REFUSED: {
    id: 'app.sml.coverEditor.ai.fail.refused',
    defaultMessage: 'The AI cannot make this picture. Change the words or a picture that it follows.',
  },
  IMAGE_FAILED: { id: 'app.sml.coverEditor.ai.fail.failed', defaultMessage: 'The pictures did not work. Try again.' },
  BUDGET_EXCEEDED: {
    id: 'app.sml.coverEditor.ai.fail.budget',
    defaultMessage: 'AI pictures are paused for now. Use the bank or your own picture.',
  },
  STALLED: { id: 'app.sml.coverEditor.ai.fail.stalled', defaultMessage: 'The pictures took too long. Try again.' },
});

export const useAsCoverErrorMessages: Record<UseAsCoverError, MessageDescriptor> = defineMessages({
  draw: {
    id: 'app.sml.coverEditor.use.error.draw',
    defaultMessage: 'Your browser could not draw the cover file. Pick another picture or try again.',
  },
  upload: { id: 'app.sml.coverEditor.use.error.upload', defaultMessage: 'The cover file did not upload. Try again.' },
  apply: {
    id: 'app.sml.coverEditor.use.error.apply',
    defaultMessage: 'The song did not take the new cover. Try again.',
  },
  demo: { id: 'app.sml.coverEditor.use.error.demo', defaultMessage: 'The example artist keeps its cover.' },
  docInvalid: {
    id: 'app.sml.coverEditor.use.error.docInvalid',
    defaultMessage: 'This design cannot be saved. Undo the last change, or start a new version, and try again.',
  },
  notImage: {
    id: 'app.sml.coverEditor.use.error.notImage',
    defaultMessage: 'The cover file is not a picture that stores take. Try again.',
  },
  notSquare: {
    id: 'app.sml.coverEditor.use.error.notSquare',
    defaultMessage: 'The cover file is not square. Try again; if it happens again, try another browser.',
  },
  tooSmall: {
    id: 'app.sml.coverEditor.use.error.tooSmall',
    defaultMessage:
      'Your browser made a file smaller than 1400 px, and stores refuse it. Try another browser or a computer.',
  },
  notOfAccount: {
    id: 'app.sml.coverEditor.use.error.notOfAccount',
    defaultMessage: 'The cover file belongs to another artist. Switch to the artist of this song and try again.',
  },
});
